import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LOGO } from "../lib/assets";
import { scrollToId } from "../lib/scroll";
import { useEnquiry } from "../context/EnquiryContext";

const LINKS = [
  { label: "Services", id: "services" },
  { label: "How It Works", to: "/how-it-works" },
  { label: "Portfolio", id: "portfolio" },
  { label: "About", id: "about" },
  { label: "Contact", id: "contact" },
];

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { openEnquiry } = useEnquiry();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToSection = (id) => {
    setMenuOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 350);
    } else {
      scrollToId(id);
    }
  };

  const handleLink = (link) => {
    if (link.to) {
      setMenuOpen(false);
      navigate(link.to);
    } else {
      goToSection(link.id);
    }
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 left-0 right-0 z-[80] transition-all duration-300 ${
          scrolled ? "bg-warm/90 backdrop-blur-md border-b border-navy/10" : "bg-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-24 flex items-center justify-between">
          <Link to="/" onClick={() => window.scrollTo(0, 0)} className="flex items-center" data-testid="logo-link">
            <img src={LOGO} alt="DIDIT — Home Interiors & Modular Interiors in Delhi NCR" className="h-16 md:h-20 w-auto object-contain" />
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {LINKS.map((l) => (
              <button
                key={l.label}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => handleLink(l)}
                className="text-sm font-medium text-navy/80 hover:text-terracotta transition-colors relative group"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-terracotta group-hover:w-full transition-all duration-300" />
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="header-consultation-btn"
              onClick={() => openEnquiry("consultation", "header")}
              className="hidden sm:inline-flex rounded-full bg-navy text-warm px-6 py-2.5 text-sm font-medium hover:bg-terracotta transition-colors"
            >
              Get Free Consultation
            </button>
            <button
              className="lg:hidden w-10 h-10 flex items-center justify-center text-navy"
              onClick={() => setMenuOpen(true)}
              data-testid="mobile-menu-open"
              aria-label="Menu"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile menu */}
      {menuOpen && (
        <motion.div
          className="fixed inset-0 z-[95] bg-navy text-warm flex flex-col p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          data-testid="mobile-menu"
        >
          <div className="flex justify-between items-center h-[72px]">
            <span className="font-display text-xl tracking-wide">DIDIT</span>
            <button onClick={() => setMenuOpen(false)} className="w-10 h-10 flex items-center justify-center" data-testid="mobile-menu-close">
              <X size={24} />
            </button>
          </div>
          <nav className="flex flex-col gap-2 mt-10">
            {LINKS.map((l, i) => (
              <motion.button
                key={l.label}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 * i }}
                onClick={() => handleLink(l)}
                className="text-left font-display text-4xl font-light py-2 border-b border-warm/10 hover:text-terracotta transition-colors"
              >
                {l.label}
              </motion.button>
            ))}
          </nav>
          <button
            onClick={() => { setMenuOpen(false); openEnquiry("consultation", "mobile-menu"); }}
            className="mt-auto rounded-full bg-terracotta text-warm px-8 py-4 font-medium"
            data-testid="mobile-consultation-btn"
          >
            Get Free Consultation
          </button>
        </motion.div>
      )}
    </>
  );
}
